import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Input,
  Select,
  Row,
  Col,
  message,
  Alert,
  Table,
  Dropdown,
  Tooltip,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  GiftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ShoppingOutlined,
  PlusOutlined,
  EllipsisOutlined,
  SearchOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { EmptyState, spacing } from '../design-system';
import { computeVoucherStats, type VoucherLike } from '../utils/voucherHelpers';
import { formatVnDate } from '../utils/vnDateTime';

// Types (match API response - snake_case from backend)
interface Voucher {
  id: number;
  code: string;
  name?: string;
  restaurant_id: number | null;
  restaurant_name?: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  minimum_order_amount?: number | null;
  max_total_usage: number;
  total_used: number;
  remaining?: number;
  is_active: boolean;
  is_public?: boolean;
  valid_from: string;
  valid_until: string;
  created_at?: string;
  metadata?: { restrictions?: string[] };
}

interface VoucherStats {
  totalVouchers: number;
  activeNow: number;
  totalRedeemed: number;
  expired: number;
  hasRedeemedData: boolean;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// --- Helpers ---
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatDateRange(start: string, end: string): string {
  const d1 = formatVnDate(start, 'DD MMM YYYY');
  const d2 = formatVnDate(end, 'DD MMM YYYY');
  if (d1 === '—' || d2 === '—') return '–';
  return `${d1} - ${d2}`;
}

function getVoucherStatus(
  validFrom: string,
  validUntil: string
): 'scheduled' | 'active' | 'expired' {
  const now = Date.now();
  const start = new Date(validFrom).getTime();
  const end = new Date(validUntil).getTime();
  if (isNaN(start) || isNaN(end)) return 'expired';
  if (now < start) return 'scheduled';
  if (now > end) return 'expired';
  return 'active';
}

function daysUntilStart(validFrom: string): number {
  const start = new Date(validFrom).getTime();
  const now = Date.now();
  if (isNaN(start)) return 0;
  return Math.max(0, Math.ceil((start - now) / (24 * 60 * 60 * 1000)));
}

// --- VoucherStatCard ---
interface VoucherStatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  iconBgColor: string;
  iconColor: string;
  loading?: boolean;
  /** When true, show "N/A" instead of 0 (e.g. no redemption data) */
  showNA?: boolean;
}

const VoucherStatCard: React.FC<VoucherStatCardProps> = ({
  title,
  value,
  icon,
  iconBgColor,
  iconColor,
  loading,
  showNA,
}) => {
  const displayValue = showNA
    ? 'N/A'
    : typeof value === 'number' && !Number.isNaN(value)
    ? value
    : 0;
  const valueNode =
    typeof displayValue === 'string' ? (
      <Tooltip title="No redemption data">
        <span>{displayValue}</span>
      </Tooltip>
    ) : (
      displayValue.toLocaleString()
    );
  return (
    <Card className="voucher-stat-card" loading={loading}>
      {!loading && (
        <>
          <div
            className="voucher-stat-card-icon-wrap"
            style={{ backgroundColor: iconBgColor, color: iconColor }}
          >
            {icon}
          </div>
          <div className="voucher-stat-card-value">{valueNode}</div>
          <div className="voucher-stat-card-title">{title}</div>
        </>
      )}
    </Card>
  );
};

// --- VoucherStatusBadge (derived from dates) ---
const VoucherStatusBadge: React.FC<{
  validFrom: string;
  validUntil: string;
}> = ({ validFrom, validUntil }) => {
  const status = getVoucherStatus(validFrom, validUntil);
  const dotColor =
    status === 'active'
      ? '#52c41a'
      : status === 'scheduled'
      ? '#faad14'
      : '#ff4d4f';
  const label =
    status === 'active'
      ? 'Active'
      : status === 'scheduled'
      ? 'Scheduled'
      : 'Expired';
  return (
    <span className="voucher-status-badge">
      <span
        className="voucher-status-dot"
        style={{ backgroundColor: dotColor }}
        aria-hidden
      />
      {label}
    </span>
  );
};

// --- DiscountBadge (pill) ---
const DiscountBadge: React.FC<{
  discountType: string;
  discountValue: number;
}> = ({ discountType, discountValue }) => {
  const val =
    typeof discountValue === 'number' && !Number.isNaN(discountValue)
      ? discountValue
      : 0;
  let text = '';
  if (discountType === 'percentage') {
    text = `${val}% OFF`;
  } else if (discountType === 'fixed_amount') {
    text = `${currencyFormatter.format(val)} Flat`;
  } else {
    text = `${val}% OFF`;
  }
  return <span className="voucher-discount-badge">{text}</span>;
};

// --- VoucherFilterBar ---
interface VoucherFilterBarProps {
  searchValue: string;
  onSearchChange: (v: string) => void;
  onSearch: (v: string) => void;
  merchantOptions: Array<{ value: number | '' | 'platform'; label: string }>;
  merchantValue: number | '' | 'platform';
  onMerchantChange: (v: number | '' | 'platform') => void;
  typeValue: string;
  onTypeChange: (v: string) => void;
  onCreateClick: () => void;
}

const VoucherFilterBar: React.FC<VoucherFilterBarProps> = ({
  searchValue,
  onSearchChange,
  onSearch,
  merchantOptions,
  merchantValue,
  onMerchantChange,
  typeValue,
  onTypeChange,
  onCreateClick,
}) => (
  <div className="voucher-filter-bar">
    <div className="voucher-filter-bar-left">
      <Input
        placeholder="Search by voucher code"
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        value={searchValue}
        onChange={e => onSearchChange(e.target.value)}
        onPressEnter={() => onSearch(searchValue)}
        allowClear
        style={{ width: 240, maxWidth: '100%' }}
      />
      <Select
        placeholder="All Merchants"
        options={merchantOptions}
        value={merchantValue === '' ? undefined : merchantValue}
        onChange={v => onMerchantChange(v ?? ('' as const))}
        allowClear
        style={{ width: 180, minWidth: 140 }}
      />
      <Select
        placeholder="All Types"
        options={[
          { value: '', label: 'All Types' },
          { value: 'percentage', label: 'Percentage' },
          { value: 'fixed_amount', label: 'Fixed amount' },
        ]}
        value={typeValue || undefined}
        onChange={v => onTypeChange(v ?? '')}
        allowClear
        style={{ width: 160, minWidth: 120 }}
      />
    </div>
    <Button type="primary" icon={<PlusOutlined />} onClick={onCreateClick}>
      Create New Voucher
    </Button>
  </div>
);

// --- Main page ---
const FETCH_LIMIT = 500;

const Vouchers: React.FC = () => {
  const [rawVouchers, setRawVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [hasRemainingFilter, setHasRemainingFilter] = useState<string>('');
  const [merchantFilter, setMerchantFilter] = useState<
    number | '' | 'platform'
  >('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchVouchers = async (): Promise<void> => {
    try {
      setLoading(true);
      const params: Record<string, string | number | boolean> = {
        page: 1,
        limit: FETCH_LIMIT,
        // Cache-busting: tránh browser/Redis trả về danh sách cũ sau khi tạo voucher
        _t: Date.now(),
      };
      if (searchText) params.search = searchText;
      if (statusFilter) params.isActive = statusFilter === 'active';

      const response = await api.get('/discount-codes/all', { params });
      const data: Voucher[] = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      setRawVouchers(data);
      setError(null);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      const errorMessage =
        ax.response?.data?.message || 'Failed to load vouchers';
      setError(errorMessage);
      message.error(errorMessage);
      setRawVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, [searchText, statusFilter]);

  const filteredVouchers = useMemo(() => {
    let list = [...rawVouchers];

    if (hasRemainingFilter === 'yes') {
      list = list.filter(v => {
        const remaining = (v.max_total_usage || 0) - (v.total_used || 0);
        return remaining > 0;
      });
    } else if (hasRemainingFilter === 'no') {
      list = list.filter(v => {
        const remaining = (v.max_total_usage || 0) - (v.total_used || 0);
        return remaining <= 0;
      });
    }

    if (merchantFilter !== '' && merchantFilter !== 'platform') {
      list = list.filter(v => v.restaurant_id === merchantFilter);
    } else if (merchantFilter === 'platform') {
      list = list.filter(v => v.restaurant_id == null);
    }

    if (typeFilter) {
      list = list.filter(v => v.discount_type === typeFilter);
    }

    return list;
  }, [rawVouchers, hasRemainingFilter, merchantFilter, typeFilter]);

  const stats = useMemo((): VoucherStats => {
    const computed = computeVoucherStats(
      filteredVouchers as VoucherLike[],
      Date.now()
    );
    return {
      totalVouchers: computed.total,
      activeNow: computed.activeNow,
      totalRedeemed: computed.totalRedeemed,
      expired: computed.expired,
      hasRedeemedData: computed.hasRedeemedData,
    };
  }, [filteredVouchers]);

  const paginatedVouchers = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filteredVouchers.slice(start, start + pagination.limit);
  }, [filteredVouchers, pagination.page, pagination.limit]);

  useEffect(() => {
    setPagination(prev => {
      const total = filteredVouchers.length;
      const pages = Math.ceil(total / prev.limit) || 1;
      const page = Math.min(prev.page, pages || 1);
      return { ...prev, total, pages, page };
    });
  }, [filteredVouchers.length]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [merchantFilter, typeFilter, hasRemainingFilter]);

  const merchantOptions = useMemo(() => {
    const opts: Array<{ value: number | '' | 'platform'; label: string }> = [
      { value: '', label: 'All Merchants' },
    ];
    const seen = new Set<number | null>();
    const hasPlatform = rawVouchers.some(v => v.restaurant_id == null);
    if (hasPlatform) {
      opts.push({ value: 'platform', label: 'Platform Wide' });
    }
    rawVouchers.forEach(v => {
      const id = v.restaurant_id;
      if (id != null && !seen.has(id)) {
        seen.add(id);
        const name = v.restaurant_name ?? `Merchant ${id}`;
        opts.push({ value: id, label: name });
      }
    });
    return opts;
  }, [rawVouchers]);

  const restaurantNameMap = useMemo(() => {
    const m = new Map<number, string>();
    rawVouchers.forEach(v => {
      if (v.restaurant_id != null && v.restaurant_name) {
        m.set(v.restaurant_id, v.restaurant_name);
      }
    });
    return m;
  }, [rawVouchers]);

  const handleSearch = (value?: string) => {
    const v = value !== undefined ? value : searchInput.trim();
    setSearchText(v);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSearchInput('');
    setStatusFilter('');
    setHasRemainingFilter('');
    setMerchantFilter('');
    setTypeFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const navigate = useNavigate();
  const handleCreateNew = () => {
    navigate('/vouchers/create');
  };

  const actionMenuItems = (record: Voucher): MenuProps['items'] => [
    {
      key: 'view',
      label: 'View details',
      onClick: () =>
        navigate(`/vouchers/${record.id}`, { state: { voucher: record } }),
    },
  ];

  const start = (pagination.page - 1) * pagination.limit;
  const end = Math.min(
    start + paginatedVouchers.length,
    start + pagination.limit
  );
  const totalDisplay = pagination.total;
  const paginationInfo =
    totalDisplay > 0
      ? `Showing ${start + 1} to ${end} of ${totalDisplay} results`
      : 'Showing 0 to 0 of 0 results';

  const columns = [
    {
      title: 'Voucher Code',
      key: 'code',
      width: 200,
      ellipsis: true,
      render: (_: unknown, record: Voucher) => {
        const minSpend = record.minimum_order_amount;
        const hasFirstOrder =
          record.metadata?.restrictions?.includes('first_order_only');
        const hasVip = record.metadata?.restrictions?.some(r =>
          /vip|vi_p/i.test(String(r))
        );
        const status = getVoucherStatus(record.valid_from, record.valid_until);
        const subLines: string[] = [];
        if (minSpend != null && Number(minSpend) > 0) {
          subLines.push(
            `Min spend ${currencyFormatter.format(Number(minSpend))}`
          );
        }
        if (hasFirstOrder) subLines.push('First order only');
        if (hasVip) subLines.push('VIP Users');
        if (status === 'expired') subLines.push('Expired');
        return (
          <div className="voucher-code-cell">
            <span className="voucher-code-bold">{record.code}</span>
            {subLines.length > 0 && (
              <div className="voucher-code-sub">{subLines.join(' · ')}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Campaign Name',
      key: 'name',
      width: 180,
      ellipsis: true,
      render: (_: unknown, record: Voucher) => (
        <span>{record.name ?? '–'}</span>
      ),
    },
    {
      title: 'Merchant Name',
      key: 'merchant',
      width: 160,
      ellipsis: true,
      render: (_: unknown, record: Voucher) => {
        const name =
          record.restaurant_id == null
            ? 'Platform Wide'
            : record.restaurant_name ??
              restaurantNameMap.get(record.restaurant_id) ??
              `Merchant ${record.restaurant_id}`;
        return <span>{name}</span>;
      },
    },
    {
      title: 'Discount Type',
      key: 'discount_type',
      width: 140,
      render: (_: unknown, record: Voucher) => (
        <DiscountBadge
          discountType={record.discount_type}
          discountValue={record.discount_value}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: unknown, record: Voucher) => (
        <VoucherStatusBadge
          validFrom={record.valid_from}
          validUntil={record.valid_until}
        />
      ),
    },
    {
      title: 'Validity Period',
      key: 'validity',
      width: 220,
      render: (_: unknown, record: Voucher) => {
        const status = getVoucherStatus(record.valid_from, record.valid_until);
        if (status === 'scheduled') {
          const days = daysUntilStart(record.valid_from);
          return (
            <span style={{ color: '#6b7280' }}>
              Starts in {days} day{days !== 1 ? 's' : ''}
            </span>
          );
        }
        return formatDateRange(record.valid_from, record.valid_until);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_: unknown, record: Voucher) => (
        <Dropdown
          menu={{ items: actionMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<EllipsisOutlined />}
            size="small"
            aria-label={`Actions for ${record.code}`}
            onClick={e => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: spacing.lg, minWidth: 0 }}>
      <header className="voucher-page-header">
        <Breadcrumbs
          items={[
            { path: '/', label: 'Dashboard' },
            { path: '', label: 'Vouchers' },
          ]}
          showHome={false}
        />
        <h1
          style={{
            margin: 0,
            marginTop: spacing.sm,
            fontSize: 24,
            fontWeight: 700,
          }}
        >
          Voucher Management
        </h1>
      </header>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: spacing.md }}
        />
      )}

      <div className="voucher-stat-cards">
        <VoucherStatCard
          title="Total Vouchers"
          value={stats.totalVouchers}
          icon={<GiftOutlined style={{ fontSize: 22 }} />}
          iconBgColor="#ede9fe"
          iconColor="#5b21b6"
          loading={loading}
        />
        <VoucherStatCard
          title="Active Now"
          value={stats.activeNow}
          icon={<CheckCircleOutlined style={{ fontSize: 22 }} />}
          iconBgColor="#d1fae5"
          iconColor="#059669"
          loading={loading}
        />
        <VoucherStatCard
          title="Total Redeemed"
          value={stats.totalRedeemed}
          icon={<ShoppingOutlined style={{ fontSize: 22 }} />}
          iconBgColor="#dbeafe"
          iconColor="#2563eb"
          loading={loading}
          showNA={!stats.hasRedeemedData && !loading}
        />
        <VoucherStatCard
          title="Expired"
          value={stats.expired}
          icon={<ClockCircleOutlined style={{ fontSize: 22 }} />}
          iconBgColor="#fee2e2"
          iconColor="#dc2626"
          loading={loading}
        />
      </div>

      <Card className="voucher-table-card">
        <VoucherFilterBar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          onSearch={handleSearch}
          merchantOptions={merchantOptions}
          merchantValue={merchantFilter}
          onMerchantChange={setMerchantFilter}
          typeValue={typeFilter}
          onTypeChange={setTypeFilter}
          onCreateClick={handleCreateNew}
        />

        {loading && rawVouchers.length === 0 ? (
          <Table
            columns={columns}
            dataSource={[]}
            rowKey="id"
            loading
            pagination={false}
            scroll={{ x: 'max-content' }}
            onRow={() => ({ style: { cursor: 'pointer' } })}
          />
        ) : filteredVouchers.length === 0 ? (
          <EmptyState
            description={
              searchText ||
              statusFilter ||
              hasRemainingFilter ||
              merchantFilter ||
              typeFilter
                ? 'No vouchers match the current filters'
                : 'No vouchers yet'
            }
            onRefresh={fetchVouchers}
            onClearFilters={
              searchText ||
              statusFilter ||
              hasRemainingFilter ||
              merchantFilter ||
              typeFilter
                ? handleClearFilters
                : undefined
            }
          />
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={paginatedVouchers}
              rowKey="id"
              loading={loading}
              pagination={false}
              scroll={{ x: 'max-content' }}
              onRow={record => ({
                onClick: () =>
                  navigate(`/vouchers/${record.id}`, {
                    state: { voucher: record },
                  }),
                style: { cursor: 'pointer' },
              })}
            />
            <div className="voucher-pagination-wrap">
              <span className="voucher-pagination-info">{paginationInfo}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  disabled={pagination.page <= 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  Previous
                </Button>
                <Button
                  disabled={
                    pagination.page >= pagination.pages ||
                    pagination.pages === 0
                  }
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default Vouchers;
